"use client";

import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  useToast,
  IconButton,
  HStack,
  Divider,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  FormControl,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import { ChevronDownIcon, DeleteIcon } from "@chakra-ui/icons";
import { Copy, Phone } from "lucide-react";

import type { PhoneNumber } from "../../services/phoneNumberService.ts";
import { phoneNumberService } from "../../services/phoneNumberService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";
import { EditablePhoneNumberName } from "./EditablePhoneNumberName.tsx";
import { MakeOutboundCallDialog } from "./MakeOutboundCallDialog.tsx";

interface PhoneNumberDetailsProps {
  phoneNumber: PhoneNumber;
  agents: Array<{ id: string; name: string }>;
  onUpdate: () => void;
  onDelete: () => void;
}

interface CustomSelectProps {
  label: string;
  value: string;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  onSelect: (value: string) => void;
  isDisabled: boolean;
  cardBg: string;
  cardComponentsBg: string;
  textColor: string;
  borderColor: string;
  isDark: boolean;
}

// A custom select component built with Chakra UI's Menu components for full styling control.
const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  value,
  placeholder,
  options,
  onSelect,
  isDisabled,
  cardBg,
  cardComponentsBg,
  textColor,
  borderColor,
  isDark,
}) => {
  const selectedOption = options.find((opt) => opt.value === value);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  return (
    <Box>
      <Text mb={2} fontWeight="medium" color={textColor}>
        {label}
      </Text>
      <Menu>
        <MenuButton
          as={Button}
          ref={buttonRef}
          rightIcon={<ChevronDownIcon />}
          isDisabled={isDisabled}
          bg={cardBg}
          color={textColor}
          borderColor={borderColor}
          borderWidth="1px"
          _hover={{ bg: cardBg }}
          _expanded={{ bg: cardBg }}
          w="100%"
          textAlign="left"
        >
          {selectedOption ? selectedOption.label : placeholder}
        </MenuButton>
        <MenuList
          bg={cardBg}
          borderColor={borderColor}
          minW={
            buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "100%"
          }
        >
          <MenuItem
            onClick={() => onSelect("")}
            _hover={{ bg: isDark ? "gray.700" : "gray.300" }}
            bg={cardComponentsBg}
            color={textColor}
          >
            {placeholder}
          </MenuItem>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => onSelect(option.value)}
              _hover={{ bg: isDark ? "gray.700" : "gray.300" }}
              bg={cardComponentsBg}
              color={textColor}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

const getProviderDisplay = (type: string): string => {
  switch (type) {
    case "retell-twilio":
      return "Twilio";
    case "retell-telnyx":
      return "Telnyx";
    case "custom":
      return "Custom telephony";
    default:
      return type;
  }
};

export const PhoneNumberDetails: React.FC<PhoneNumberDetailsProps> = ({
  phoneNumber,
  agents,
  onUpdate,
  onDelete,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const toast = useToast();

  // Add this state for the outbound call dialog
  const {
    isOpen: isCallDialogOpen,
    onOpen: onCallDialogOpen,
    onClose: onCallDialogClose,
  } = useDisclosure();

  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Define theme-based colors
  const cardBg = "transparent";
  const textColor = isDark ? "whiteAlpha.900" : "gray.900";
  const cardComponentsBg = isDark ? "gray.800" : "white";
  const borderColor = isDark ? "gray.100" : "gray.500";

  const [inboundWebhookUrl, setInboundWebhookUrl] = useState<string>(
    phoneNumber.inbound_webhook_url || ""
  );

  useEffect(() => {
    setInboundWebhookUrl(phoneNumber.inbound_webhook_url || "");
  }, [phoneNumber]);

  const handleUpdate = async (field: string, value: string | null) => {
    setIsUpdating(true);
    try {
      await phoneNumberService.update(phoneNumber.phone_number, {
        [field]: value,
      });
      onUpdate();
      toast({
        title: "Phone number updated",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error updating phone number",
        description:
          error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInboundWebhookUrlUpdate = useCallback(async () => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      await phoneNumberService.update(phoneNumber.phone_number, {
        inbound_webhook_url: inboundWebhookUrl || null,
      });

      toast({
        title: "Inbound Webhook URL updated",
        status: "success",
        duration: 2000,
      });
      onUpdate();
    } catch (error: any) {
      console.error("Error updating inbound webhook URL:", error);
      toast({
        title: "Error updating inbound webhook URL",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });

      // Reset to initial value on error
      setInboundWebhookUrl(phoneNumber.inbound_webhook_url || "");
    } finally {
      setIsUpdating(false);
    }
  }, [
    phoneNumber.phone_number,
    inboundWebhookUrl,
    isUpdating,
    toast,
    phoneNumber.inbound_webhook_url,
    onUpdate,
  ]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await phoneNumberService.delete(phoneNumber.phone_number);
      onDelete();
      toast({
        title: "Phone number deleted",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error deleting phone number",
        description:
          error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
      onClose();
    }
  };

  // Prepare the options for the custom selects
  const agentOptions = agents.map((agent) => ({
    value: agent.id,
    label: agent.name,
  }));

  const handleCopyId = () => {
    navigator.clipboard.writeText(phoneNumber.phone_number);
    toast({
      title: "Phone number ID copied",
      status: "success",
      duration: 2000,
    });
  };

  return (
    <Box p={6} bg={cardBg} borderRadius="md">
      <VStack align="stretch" spacing={6}>
        <Box>
          {/* Row: EditablePhoneNumberName on the left, Delete button on the right (aligned at bottom) */}
          <HStack alignItems="flex-end" justifyContent="space-between">
            <EditablePhoneNumberName
              phoneNumber={phoneNumber.phone_number}
              nickname={phoneNumber.nickname}
              prettyNumber={phoneNumber.phone_number_pretty}
              onUpdate={onUpdate}
            />

            <HStack spacing={2}>
              <IconButton
                aria-label="Make outbound call"
                icon={<Phone size={16} />}
                onClick={onCallDialogOpen}
                variant="outline"
                colorScheme="green"
                color={isDark ? "green.400" : "green.300"}
                _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
                size="sm"
                isDisabled={!phoneNumber.outbound_agent_id}
                title={
                  phoneNumber.outbound_agent_id
                    ? "Make outbound call"
                    : "Configure outbound agent first"
                }
              />
              <IconButton
                aria-label="Delete phone number"
                icon={<DeleteIcon />}
                onClick={onOpen}
                variant="outline"
                colorScheme="red"
                color={isDark ? "red.300" : "red.500"}
                _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
                size="sm"
              />
            </HStack>
          </HStack>

          {/* Below row: ID / Provider info */}
          <HStack
            spacing={4}
            mt={2}
            color={isDark ? "gray.400" : "gray.600"}
            fontSize="sm"
          >
            <Text color="gray.500" fontSize="sm">
              ID: {phoneNumber.phone_number}
              <IconButton
                aria-label="Copy ID"
                icon={<Copy size={12} />}
                onClick={handleCopyId}
                size="xs"
                variant="ghost"
                color="gray.500"
              />
              &nbsp;&bull;&nbsp; Provider:{" "}
              {getProviderDisplay(phoneNumber.phone_number_type)}
            </Text>
          </HStack>
        </Box>

        <Divider />

        <VStack align="stretch" spacing={4}>
          <CustomSelect
            label="Inbound call agent"
            value={phoneNumber.inbound_agent_id || ""}
            placeholder="None (disable inbound)"
            onSelect={(val) => handleUpdate("inbound_agent_id", val || null)}
            isDisabled={isUpdating}
            cardBg={cardBg}
            cardComponentsBg={cardComponentsBg}
            textColor={textColor}
            borderColor={borderColor}
            isDark={isDark}
            options={agentOptions}
          />

          <CustomSelect
            label="Outbound call agent"
            value={phoneNumber.outbound_agent_id || ""}
            placeholder="None (disable outbound)"
            onSelect={(val) => handleUpdate("outbound_agent_id", val || null)}
            isDisabled={isUpdating}
            cardBg={cardBg}
            cardComponentsBg={cardComponentsBg}
            textColor={textColor}
            borderColor={borderColor}
            isDark={isDark}
            options={agentOptions}
          />

          <FormControl>
            <FormLabel
              fontSize="sm"
              fontWeight="medium"
              color={isDark ? "gray.300" : "gray.700"}
            >
              Inbound Call Webhook URL
            </FormLabel>
            <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"} mb={2}>
              The webhook for retrieving dynamic variables for inbound phone
              calls.
              {/* {" "} */}
              {/* <Link
                href="https://docs.retellai.com/build/dynamic-variables#add-and-test-dynamic-variables"
                color={isDark ? "gray.300" : "gray.600"}
                isExternal
              >
                (Learn more) <ExternalLinkIcon mx="2px" />
              </Link> */}
            </Text>
            <Input
              value={inboundWebhookUrl}
              onChange={(e) => setInboundWebhookUrl(e.target.value)}
              onBlur={handleInboundWebhookUrlUpdate}
              placeholder="https://your-webhook-url.com/inbound"
              size="sm"
              bg={isDark ? "gray.700" : "white"}
              borderColor={isDark ? "gray.600" : "gray.300"}
            />
          </FormControl>
        </VStack>

        <Divider />
      </VStack>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardComponentsBg}>
            <AlertDialogHeader color={textColor}>
              Delete Phone Number
            </AlertDialogHeader>
            <AlertDialogBody color={textColor}>
              Are you sure you want to delete this phone number? This action
              cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                colorScheme={isDark ? "black" : "white"}
                textColor={isDark ? "whiteAlpha" : "gray.800"}
                _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
                variant="ghost"
                ref={cancelRef}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={isDeleting}
                background={isDark ? "red.400" : "red.300"}
                _hover={{ bg: isDark ? "red.600" : "red.500" }}
                textColor={isDark ? "whiteAlpha" : "gray.800"}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Add the outbound call dialog */}
      <MakeOutboundCallDialog
        isOpen={isCallDialogOpen}
        onClose={onCallDialogClose}
        phoneNumber={phoneNumber.phone_number}
        agentId={phoneNumber.outbound_agent_id}
      />
    </Box>
  );
};
